import { Button, Skeleton } from "antd"
import dayjs from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import {
  type ActivityFeedItem,
  type FeedEntry,
  isActivityFeedItem
} from "../../../components/widgets/types"
import { homeColor, homeText } from "../home-theme.ts"
import { HomeCard, type HomeCardEdit } from "./HomeCard.tsx"
import { ClockIcon } from "./home-icons.tsx"

/** The preview's length. Everything the feed returned is one View all away. */
export const ACTIVITY_PREVIEW_ROWS = 5

/**
 * The activity events out of a resolved feed payload, newest first as the
 * server sent them. `null` means the widget has not resolved yet — a
 * different state from an empty feed, and the card says different things.
 */
export const activityItemsFrom = (
  payload: unknown
): ActivityFeedItem[] | null => {
  const feed = (payload as { feed?: { items?: unknown } } | undefined)?.feed
  if (!feed || !Array.isArray(feed.items)) return null
  return (feed.items as FeedEntry[]).filter(isActivityFeedItem)
}

const ActivityRow = ({ item }: { item: ActivityFeedItem }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const at = dayjs(item.timestamp)
  const relative = at.isValid() ? at.fromNow() : item.timestamp
  const open = item.drill_to ? () => navigate(item.drill_to!) : undefined

  return (
    <div
      className="home-activity__row"
      data-testid="home-activity-row"
      role={open ? "button" : undefined}
      tabIndex={open ? 0 : undefined}
      onClick={open}
      onKeyDown={
        open
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault()
                open()
              }
            }
          : undefined
      }
    >
      <span
        className="truncate"
        title={item.title}
        style={{ ...homeText.body, color: homeColor.ink, flex: 1, minWidth: 0 }}
      >
        {item.title}
      </span>
      <time
        dateTime={at.isValid() ? at.toISOString() : undefined}
        title={at.isValid() ? at.format("LLL") : undefined}
        aria-label={t("portfolio.feed.occurredAt", "Occurred at {{time}}", {
          time: at.isValid() ? at.format("LLL") : relative
        })}
        style={{
          ...homeText.meta,
          color: homeColor.inkMuted,
          whiteSpace: "nowrap",
          flexShrink: 0
        }}
      >
        {relative}
      </time>
    </div>
  )
}

interface HomeActivityCardProps {
  /** The resolved feed payload; `undefined` while it is still loading. */
  payload: unknown
  error?: boolean
  onRetry?: () => void
  /** Opens the whole feed. */
  onViewAll: () => void
  edit?: HomeCardEdit | null
  /** A user's own name for the widget. */
  title?: string
}

/**
 * Recent activity as a compact preview: the latest five events, one line
 * each, divided by hairlines. The full widget drew every event the feed
 * returned as its own bordered box, which made this card twice the height of
 * the Lead pipeline beside it and left a block of empty space under the
 * pipeline.
 */
export const HomeActivityCard = ({
  payload,
  error,
  onRetry,
  onViewAll,
  edit,
  title
}: HomeActivityCardProps) => {
  const { t } = useTranslation()
  const items = activityItemsFrom(payload)
  const preview = items?.slice(0, ACTIVITY_PREVIEW_ROWS) ?? []

  return (
    <HomeCard
      testId="home-activity-card"
      edit={edit}
      bodyPadding={0}
      icon={<ClockIcon width={14} height={14} />}
      title={title ?? t("home.activity.title", "Recent activity")}
      actions={
        items && items.length > 0 ? (
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={onViewAll}
            data-testid="home-activity-view-all"
          >
            {items.length > preview.length
              ? t("home.approvals.viewAllCount", "View all ({{count}})", {
                  count: items.length
                })
              : t("common.viewAll", "View all")}
          </Button>
        ) : null
      }
    >
      {error && !items ? (
        <div style={{ padding: "20px 16px", textAlign: "center" }} role="alert">
          <div style={{ ...homeText.body, color: homeColor.inkMuted }}>
            {t("home.activity.error", "Recent activity could not be loaded.")}
          </div>
          {onRetry ? (
            <Button type="link" onClick={onRetry}>
              {t("common.retry", "Retry")}
            </Button>
          ) : null}
        </div>
      ) : !items ? (
        <div style={{ padding: 16 }} data-testid="home-activity-loading">
          <Skeleton active paragraph={{ rows: 4 }} title={false} />
        </div>
      ) : preview.length === 0 ? (
        <p
          data-testid="home-activity-empty"
          style={{
            ...homeText.meta,
            color: homeColor.inkMuted,
            margin: 0,
            padding: 16
          }}
        >
          {t("portfolio.feed.empty", "No recent activity")}
        </p>
      ) : (
        <div className="home-activity__list">
          {preview.map((item) => (
            <ActivityRow key={item.id} item={item} />
          ))}
        </div>
      )}
    </HomeCard>
  )
}
